import { call, put, select, takeLatest } from 'redux-saga/effects';
import { adventCalendarActions } from './index';
import { Api } from '$api';
import {
  CreateAdventCalendarAction,
  GetAdventCalendarAction,
  CreateAdventBundleAction,
  UpdateAdventBundleAction,
  UpdateCalendarDaysAction,
  LoadBundleForEditingAction,
} from './interface';
import { toastActions } from '$store/toast';
import { currentUserSelector } from '$store/main';
import { goBack, showModal } from '$navigation/helper';
import { RootState } from '$store/rootReducer';
import { attachActions } from '$store/attach';

function* getAdventCalendarWorker(action: GetAdventCalendarAction) {
  try {
    const userId = action.payload;
    yield put(adventCalendarActions.setLoading({ userId, loading: true }));
    const response = yield call(Api.get, `/user/${userId}/advent-calendar`);

    const data = response?.data || response;
    const calendar = data?.calendar;
    const days = data?.days || [];
    const progress = data?.progress;

    if (calendar) {
      const bundleIds = Array.isArray(days)
        ? days.map((day: any) => (day && day.bundleId ? day.bundleId : 0))
        : [];

      const calendarData = {
        ...calendar,
        userId: calendar.userId || userId,
        bundleIds,
        days,
        progress,
      };

      yield put(adventCalendarActions.setAdventCalendar(calendarData));
      yield put(adventCalendarActions.setLoading({ userId, loading: false }));
    } else {
      yield put(
        adventCalendarActions.setAdventCalendar({
          userId,
          calendar: null,
        }),
      );
      yield put(adventCalendarActions.setLoading({ userId, loading: false }));
    }
  } catch (error: any) {
    const userId = action.payload;
    yield put(
      adventCalendarActions.setAdventCalendar({
        userId,
        calendar: null,
      }),
    );
    yield put(adventCalendarActions.setLoading({ userId, loading: false }));
  }
}

function* createAdventCalendarWorker(action: CreateAdventCalendarAction) {
  try {
    yield put(toastActions.setToastLoading());

    const { title, description, bundleIds } = action.payload;
    const user = yield select(currentUserSelector);

    const response = yield call(Api.post, '/advent-calendar', {
      title,
      description,
      bundleIds,
    });

    const data = response?.data || response;
    const calendar = data?.calendar || data;
    const days = data?.days || [];
    const progress = data?.progress;

    const extractedBundleIds = Array.isArray(days)
      ? days.map((day: any) => (day && day.bundleId ? day.bundleId : 0))
      : [];

    const calendarData = {
      ...calendar,
      userId: calendar.userId || user.id,
      bundleIds: extractedBundleIds,
      days,
      progress,
    };
    yield put(adventCalendarActions.setAdventCalendar(calendarData));

    yield put(toastActions.hideToast());

    showModal('advent_calendar_configuration');
  } catch (error: any) {
    yield put(
      toastActions.setToastFail(
        error?.response?.data?.message || 'Failed to create calendar',
      ),
    );
  }
}

function* createAdventBundleWorker(action: CreateAdventBundleAction) {
  try {
    yield put(toastActions.setToastLoading());

    const { calendarId, day, name, description, price, previews, content } =
      action.payload;

    const data = yield call(Api.post, '/bundles/create', {
      name,
      description,
      price,
      priceWithoutDiscount: price,
      previews,
      content,
      isService: true,
    });

    const bundleId = data.id;

    const user = yield select(currentUserSelector);
    const calendar = yield select(
      (state: RootState) => state.adventCalendar.calendars[user.id],
    );

    if (!calendar) {
      throw new Error('Calendar not found');
    }

    const newBundleIds = [...(calendar.bundleIds || [])];

    while (newBundleIds.length < day) {
      newBundleIds.push(0);
    }

    newBundleIds[day - 1] = bundleId;

    const response = yield call(Api.put, `/advent-calendar/${calendarId}/days`, {
      bundleIds: newBundleIds,
    });

    const responseData = response?.data || response;
    const updatedCalendar = responseData?.calendar;
    const days = responseData?.days || [];
    const progress = responseData?.progress;

    if (updatedCalendar) {
      const extractedBundleIds = Array.isArray(days)
        ? days.map((day: any) => (day && day.bundleId ? day.bundleId : 0))
        : [];

      const calendarData = {
        ...updatedCalendar,
        userId: updatedCalendar.userId || user.id,
        bundleIds: extractedBundleIds,
        days,
        progress,
      };
      yield put(adventCalendarActions.setAdventCalendar(calendarData));
    }

    yield put(attachActions.clear('advent_bundle_previews'));
    yield put(attachActions.clear('advent_bundle_content'));
    yield put(toastActions.hideToast());
    goBack();
  } catch (error: any) {
    yield put(
      toastActions.setToastFail(
        error?.response?.data?.message || error?.message || 'Failed to create bundle',
      ),
    );
  }
}

function* updateAdventBundleWorker(action: UpdateAdventBundleAction) {
  try {
    yield put(toastActions.setToastLoading());

    const { bundleId, name, description, price, previews, content } = action.payload;

    yield call(Api.post, `/bundles/${bundleId}/save`, {
      name,
      description,
      price,
      priceWithoutDiscount: price,
      previews,
      content,
      isService: true,
    });

    const user = yield select(currentUserSelector);
    yield put(adventCalendarActions.getAdventCalendar(user.id));

    yield put(attachActions.clear('advent_bundle_previews'));
    yield put(attachActions.clear('advent_bundle_content'));
    yield put(toastActions.hideToast());
    goBack();
  } catch (error: any) {
    yield put(
      toastActions.setToastFail(
        error?.response?.data?.message || error?.message || 'Failed to update bundle',
      ),
    );
  }
}

function* updateCalendarDaysWorker(action: UpdateCalendarDaysAction) {
  try {
    const { calendarId, bundleIds } = action.payload;

    yield call(Api.put, `/advent-calendar/${calendarId}/days`, {
      bundleIds,
    });

    const user = yield select(currentUserSelector);
    yield put(adventCalendarActions.getAdventCalendar(user.id));
  } catch (error: any) {
    yield put(
      toastActions.setToastFail(
        error?.response?.data?.message || 'Failed to update calendar',
      ),
    );
  }
}

function* loadBundleForEditingWorker(action: LoadBundleForEditingAction) {
  try {
    const { bundleId } = action.payload;

    // Check if already loading this bundle
    const loadingBundleId = yield select(
      (state: RootState) => state.adventCalendar.loadingBundleId,
    );
    if (loadingBundleId === bundleId) {
      return; // Already loading, skip
    }

    // Check if already loaded
    const editingBundle = yield select(
      (state: RootState) => state.adventCalendar.editingBundle,
    );
    if (editingBundle?.bundleId === bundleId) {
      return; // Already loaded, skip
    }

    // Set loading state
    yield put(adventCalendarActions.setLoadingBundleId(bundleId));

    const response = yield call(Api.get, `/bundles/${bundleId}/info`);
    const { bundle, decryptKey } = response;

    // Save to store (this will also clear loadingBundleId)
    yield put(
      adventCalendarActions.setEditingBundle({
        bundleId,
        name: bundle.name,
        description: bundle.description,
        price: bundle.price,
        priceWithoutDiscount: bundle.priceWithoutDiscount,
        previews: bundle.previews,
        content: bundle.content,
        decryptKey,
      }),
    );

    // Initialize attachments
    yield put(
      attachActions.initFromAttachments({
        pickerId: 'advent_bundle_previews',
        attachments: bundle.previews,
      }),
    );
    yield put(
      attachActions.initFromAttachments({
        pickerId: 'advent_bundle_content',
        attachments: bundle.content,
      }),
    );
  } catch (error: any) {
    console.error('Failed to load bundle info:', error);
    yield put(adventCalendarActions.setLoadingBundleId(null));
    yield put(
      toastActions.setToastFail(
        error?.response?.data?.message || error?.message || 'Failed to load bundle',
      ),
    );
  }
}

export function* adventCalendarSaga() {
  yield takeLatest(adventCalendarActions.getAdventCalendar, getAdventCalendarWorker);
  yield takeLatest(
    adventCalendarActions.createAdventCalendar,
    createAdventCalendarWorker,
  );
  yield takeLatest(adventCalendarActions.createAdventBundle, createAdventBundleWorker);
  yield takeLatest(adventCalendarActions.updateAdventBundle, updateAdventBundleWorker);
  yield takeLatest(adventCalendarActions.updateCalendarDays, updateCalendarDaysWorker);
  yield takeLatest(
    adventCalendarActions.loadBundleForEditing,
    loadBundleForEditingWorker,
  );
}
